import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { fetchAssessment, resetAssessment } from '../../app/store/assessmentSlice';
import { setAllQuestionStatus, setBestPractice, setBPStatus, setCertificationControlIds, setDomain, setInclusionConfigsById, setQuestion, setQuestionnaire, setQuestions, setTopic } from '../../app/store/questionnaireAnswerSlice';
import { BestPractice } from '../../interfaces/bestPractice.interface';
import { Domain } from '../../interfaces/domain.interface';
import { InclusionConfiguration } from '../../interfaces/inclusionConfiguration.interface';
import { Question } from '../../interfaces/question.interface';
import { QuestionAnswer } from '../../interfaces/questionAnswer.interface';
import { Questionnaire } from '../../interfaces/questionnaire.interface';
import { Topic } from '../../interfaces/topic.interface';
import { buildOutQuestionnaire, fetchQuestionDependantObjects, initialBuildOutQuestionnaire } from '../../services/buildQuestionnaire';
import { QuestionnaireTypes, buildFullQuestionnaireObject } from '../../services/questionnaires';
import useGradualProgress from './useGradualProgress';
import { Box, Backdrop, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { batch } from 'react-redux';
import { BLUE_BADGE_IMAGE } from '../../services/badge';
import { AssessmentStatus, AssessmentStatusesSet } from '../../services/assessments';

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const useStyles = makeStyles(() => ({
  backdrop: {
    backgroundColor: 'rgba(0,0,0,.5)',
    zIndex: '999999999',
  },
  loaderContainer: {
    width: '500px',
    maxWidth: '90%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    color: 'white',
  },
  loaderBadgeContainer: {
    width: '100%',
    height: '402px',
    maxHeight: '90%',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loaderBadge: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
  clipPath: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    transition: 'clip-path 1s linear',
    width: '100%',
    height: '100%',
  },
  percentText: {
    fontSize: '28px',
    fontWeight: 'bold',
  },
  percentTextTitle: {
    fontSize: '20px',
    fontWeight: 'semibold',
  },
}));

export default function QuestionnaireAnswerLoader(props: {
  questionnaire: Questionnaire,
  setHideLoader: (hide: boolean) => void,
  domain?: Domain,
  topic?: Topic,
  bestPractice?: BestPractice,
}) {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const params = useParams();
  const query = useQuery();
  const [isQuestionnaireBuilderComplete, setIsQuestionnaireBuilderComplete] = useState(false);
  const assessmentId:string | undefined = params.assessmentId;
  const questionId:string | null = query.get('question');
  const {
    questionnaire,
    questions,
    preview,
    bpStatus,
    questionStatus,
    assessmentQuestionsStatus,
  } = useAppSelector(state => state.questionnaireAnswer);
  const { assessment } = useAppSelector(state => state.assessments);
  const { survey } = useAppSelector(state => state.surveys);
  const { workflow } = useAppSelector(state => state.workflow);
  const [loadingFinished, setLoadingFinished] = useState(false);
   
  const progressPercent = useGradualProgress(1, 50, 88, 98, 750, loadingFinished, props.setHideLoader, 25, 100, 2);

  const requiresAssessmentQuestionsStatus: boolean =
    Boolean(
      workflow &&
      (survey?.questionnaire as Questionnaire)?.type === QuestionnaireTypes.MPA_BEST_PRACTICE &&
      AssessmentStatusesSet.has(workflow.status as AssessmentStatus),
    );

  const hasAssessmentQuestionsStatus = useMemo(
    () => Boolean(assessmentQuestionsStatus && Object.keys(assessmentQuestionsStatus).length),
    [assessmentQuestionsStatus],
  );

  const isAssessmentQuestionStatusFulfilled: boolean = useMemo(
    () => ((!requiresAssessmentQuestionsStatus && !!workflow) || hasAssessmentQuestionsStatus),
    [requiresAssessmentQuestionsStatus, workflow, hasAssessmentQuestionsStatus],
  );

  useEffect(() => {
    if (
      isQuestionnaireBuilderComplete &&
      ((isAssessmentQuestionStatusFulfilled && !!survey?.bestPracticeStatuses?.length) || preview)
    ) {
      setLoadingFinished(true);
    }
  }, [isAssessmentQuestionStatusFulfilled, survey?.bestPracticeStatuses?.length, preview, isQuestionnaireBuilderComplete]);

  const buildQuestionnaire = useCallback(async () => {
    if (!preview && (!survey || questionnaire)) return;
    if (preview && questions !== undefined && questions.length > 0) return;

    try {
      const questionnaireObjects = await buildFullQuestionnaireObject(props.questionnaire.id);
      const builtQuestionnaire = initialBuildOutQuestionnaire( { ...questionnaireObjects, questionnaire: props.questionnaire } );
      const getQuestions = await fetchQuestionDependantObjects(false, builtQuestionnaire.allQuestions, builtQuestionnaire.allInclusions);    
      const inclusionQuestions = [ ...getQuestions.questions.map(bpq => bpq.inclusions.map(inc => (inc as InclusionConfiguration).question)).flat(1), ...getQuestions.inclusions.map(inc => (inc as InclusionConfiguration).question) ] as Question[];
      const additionalQuestionsList = inclusionQuestions.filter(incq => getQuestions.questions.findIndex(qu => qu.id === incq.id) === -1).map(qu => qu.id);
      const additionalQuestionsListNonDup = [ ...new Set(additionalQuestionsList) ];
      const restOfQuestions = await fetchQuestionDependantObjects(false, additionalQuestionsListNonDup);
      const restOfinclusionQuestions = restOfQuestions.questions.map(bpq => bpq.inclusions.map(inc => (inc as InclusionConfiguration).question)).flat(1);
      const allRestOfQuestions = [ ...restOfQuestions.questions ];
      (restOfinclusionQuestions as Question[]).forEach(incq => {
        if (allRestOfQuestions.findIndex(q => q.id === incq.id) === -1) allRestOfQuestions.push(incq);
      });
      const { questionnaire: fullQuestionnaire, certControls, questionStatusById } = buildOutQuestionnaire(builtQuestionnaire.questionnaire, [ ...getQuestions.questions, ...allRestOfQuestions ], survey ? survey.questionAnswers as QuestionAnswer[] : undefined, workflow?.workflowType);
      batch(() => {
        dispatch(setInclusionConfigsById(getQuestions.inclusions));
        dispatch(setCertificationControlIds(certControls));
        dispatch(setQuestions([ ...getQuestions.questions, ...allRestOfQuestions ]));
        dispatch(setAllQuestionStatus(questionStatusById));
        dispatch(setQuestionnaire(fullQuestionnaire));
        dispatch(setBPStatus(workflow?.workflowType));
      });
    } catch (error) {
      console.error('Error building questionnaire:', error);
    }
  }, [preview, survey, questionnaire, questions, props.questionnaire, dispatch, workflow]);

  const setValuesFromQuestion = () => {
    if (!questionnaire || !questions || !questionId) return false;

    const selectedQuestion = questions.find(cq => cq.id == questionId);
    if (!selectedQuestion) return false;

    const domainId = typeof selectedQuestion.domain === 'number' ? selectedQuestion.domain : selectedQuestion.domain.id;
    const nDomain = questionnaire.domains.find(d => d.id === domainId);
    if (!nDomain) return false;

    const topicId = typeof selectedQuestion.topic === 'number' ? selectedQuestion.topic : selectedQuestion.topic.id;
    const nTopic = nDomain.topics.find(t => t.id === topicId);
    if (!nTopic) return false;

    const bestPracticeId = typeof selectedQuestion.bestPractice === 'number' ? selectedQuestion.bestPractice : selectedQuestion.bestPractice.id;
    const nBP = nTopic.bestPractices.find(bp => bp.id === bestPracticeId);
    if (!nBP) return false;
    batch(() => {
      dispatch(setDomain(nDomain));
      dispatch(setTopic(nTopic));
      dispatch(setBestPractice(nBP));
      dispatch(setQuestion(selectedQuestion));
    });
    return true;
  };

  useEffect(() => {
    if (!survey || questionnaire) return;
  
    (async () => {
      await buildQuestionnaire();
      setIsQuestionnaireBuilderComplete(true);
    })();
  }, [buildQuestionnaire, survey]);

  useEffect(() => {
    if (!bpStatus || !Object.keys(bpStatus).length || !bpStatus[Object.keys(bpStatus)[0]].id || !isAssessmentQuestionStatusFulfilled) return;
    setLoadingFinished(true);
  }, [bpStatus, questionnaire, isAssessmentQuestionStatusFulfilled]);
  
  useEffect(() => {
    if (!params || assessment) return;
    dispatch(resetAssessment);
    if (assessmentId) dispatch(fetchAssessment(parseInt(assessmentId)));
    return () => {
      dispatch(resetAssessment);
    };
  }, [params]);

  useEffect(() => {
    if (!questionnaire) return;
    if (setValuesFromQuestion()) return;

    const domainId = props.domain ? props.domain.id : questionnaire.domains[0].id;
    const nDomain = questionnaire.domains.find(d => d.id === domainId);
    if (!nDomain) return;
    dispatch(setDomain(nDomain));

    const firstValidTopic = questionStatus ? nDomain.topics.find(t =>
      t.bestPractices.some(bp =>
        bp.questions.some(q => questionStatus[q.id]?.status !== 'hide'),
      ),
    ) : nDomain.topics[0];

    const topicId = props.topic ? props.topic.id : firstValidTopic?.id;
    const nTopic = nDomain.topics.find(t => t.id === topicId);
    if (!nTopic) return;
    dispatch(setTopic(nTopic));

    // Find the first best practice where any question is not hidden
    const firstValidBP = questionStatus && nTopic.bestPractices.find(bp =>
      bp.questions.some(q => questionStatus[q.id]?.status !== 'hide'),
    );

    // Determine the best practice ID
    const bestPracticeId = props.bestPractice
      ? props.bestPractice.id
      : firstValidBP
        ? firstValidBP.id
        : nTopic.bestPractices[0].id;

    const nBP = nTopic.bestPractices.find(bp => bp.id === bestPracticeId);
    if (!nBP) return;
    dispatch(setBestPractice(nBP));

    const nquestion = nBP.questions[0];
    if (!nquestion) return;
    dispatch(setQuestion(nquestion));
  }, [questionnaire, questionStatus]);

  return (
    <Backdrop className={classes.backdrop} open>
      <Box className={classes.loaderContainer}>
        <Box className={classes.loaderBadgeContainer}>
          <Box className={classes.clipPath} sx={{ clipPath: `polygon(0% 0%, 100% 0%, 100% ${100 - progressPercent}%, 0% ${100 - progressPercent}%);` }}><Box component='img' src={BLUE_BADGE_IMAGE} className={classes.loaderBadge} /></Box>
          <Box className={classes.clipPath} sx={{ clipPath: `polygon(0% ${100 - progressPercent}%, 0% 100%, 100% 100%, 100% ${100 - progressPercent}%)` }}><Box component='img' src={`${process.env.PUBLIC_URL}/assets/TPN_GoldLogoTransparent_1280.png`} className={classes.loaderBadge} /></Box>
        </Box>
        <Typography className={classes.percentText}>{progressPercent}%</Typography>
        <Typography className={classes.percentTextTitle}>Loading {props.questionnaire?.title}</Typography>
      </Box>
    </Backdrop>
  );
}